<template>
	<DashboardModal
		ref="modal"
		dialog
		min-width="900px"
		@modal-toggle="$emit('modal-toggle')"
	>
		<DashboardFormTitle style="display: flex">
			{{ $t("cart-view.modal-title", { 0: cart.id }) }}
		</DashboardFormTitle>
		<Grid :class="$style['form']">
			<GridColumn xl="4" sm="12">
				<BaseDropdownInput
					id="client"
					v-model="form.client"
					:list="clients"
					name="client"
					search="true"
					:show-carret="false"
					:loading="clientSuggestionsLoading"
					avoid-filtering
					:show-no-results="true"
					:label-text="$t('common.fields.client')"
					:additional-fields="additionalFields"
					@input="handleClientInput"
					@clear="handleUpdateCart('client')"
					@submit="handleUpdateCart('client')"
				/>
			</GridColumn>
			<GridColumn xl="4" sm="12">
				<BaseDropdownInput
					id="delivery-type"
					v-model="form.deliveryType"
					v-validate:deliveryType="'required'"
					:list="deliveryTypes"
					:helper-text="errors.first('delivery-type')"
					:invalid="errors.has('delivery-type')"
					name="delivery-type"
					:label-text="$t('common.fields.delivery-type')"
					@clear="handleUpdateCart"
					@submit="handleUpdateCart"
				/>
			</GridColumn>
			<GridColumn v-if="!oemCart" xl="4" sm="12">
				<BaseDropdownInput
					v-model="form.orderType"
					v-validate:orderTypeToValidate="'required'"
					:list="orderTypes"
					:helper-text="errors.first('orderType')"
					:invalid="errors.has('orderType')"
					:class="$style['input']"
					name="orderType"
					:label-text="$t('cart-view.fields.order-type')"
				/>
			</GridColumn>
		</Grid>

		<div style="margin-top: 20px;">
			<BaseTable
				v-if="cartProducts && cartProducts.length"
				:sticky-header="false"
				:show-footer="false"
				style="box-shadow: none; margin: 0"
			>
				<BaseTableRow
					:header="true"
					:show-actions="false"
					:with-padding="false"
					clean
					:show-checkbox="false"
					:indent-space="false"
					:class="$style['table-header']"
					:selected="Object.keys(checked).length"
				>
					<template #header-actions>
						<button
							:class="[$style['header-action'], $style['header-action--danger']]"
						>
							{{ $t("parts-view.table.actions.delete-selected") }}
						</button>
					</template>
					<BaseTableCol
						v-for="(column, index) in columns"
						:key="index"
						:class="[
							$style['column'],
							{ [$style['flex-end']]: column.sortKey === 'total' }
						]"
						:header="true"
						v-bind="{ span: column.span, column }"
					>
						{{ $t("oem-parts.table.column-" + column.name) }}
					</BaseTableCol>
				</BaseTableRow>

				<div>
					<BaseTableRow
						v-for="(product, i) in cartProducts"
						:key="i"
						:with-padding="false"
						:show-actions="false"
						clean
						:show-checkbox="false"
						:indent-space="false"
						:class="$style['row']"
					>
						<BaseTableCol
							v-for="(column, index) in columns"
							:key="index"
							:class="[
								$style['column'],
								{ [$style['flex-end']]: column.sortKey === 'total' }
							]"
							v-bind="{ span: column.span, column }"
						>
							<template v-if="column.name === 'manufacturer'">
								<div :class="$style['product-info']">
									<div
										:class="[$style['image-wrappper'], {
											[$style['image-wrappper--openable']]: product.logoUrl
										}]"
									>
										<img
											v-if="product.logoUrl"
											alt="logo"
											:src="product.logoUrl"
										/>
										<FontAwesomeIcon
											v-else
											:icon="['fal', 'image']"
											:class="$style['image-wrappper-placeholder']"
										/>
									</div>
									<div :class="$style['product-title']">
										<strong>{{ productName(product) }}</strong>
										<small v-if="product.parameters && (product.name || product.title)">
											{{ product.parameters }}
										</small>
										<small v-if="product.subparameters">
											{{ product.subparameters }}
										</small>
									</div>
								</div>
							</template>
							<template v-if="column.name === 'supplier'">
								{{ product.supplierName }}
							</template>
							<template v-if="column.name === 'oem-code'">
								{{ product.oemCode }}
							</template>
							<template v-if="column.name === 'quantity'">
								{{ product.quantity }} {{ $t("common.fields.units") }}
							</template>
							<template v-if="column.name === 'price'">
								{{ product.sellPrice | currency($auth().currency) }}
							</template>
							<template v-if="column.name === 'total'">
								{{ (product.sellPrice * product.quantity) | currency($auth().currency) }}
							</template>
						</BaseTableCol>
					</BaseTableRow>
				</div>
			</BaseTable>
			<div :class="$style['footer']">
				<p>
					<span>{{ $t("common.fields.total") }}: </span>
					<strong>{{ cart.totalPrice | currency($auth().currency) }}</strong>
				</p>
			</div>
			<BaseButton
				v-if="oemCart"
				:loading="submitRequesting"
				:disabled="!cartProducts || !cartProducts.length"
				style="margin-left: auto"
				type="submit"
				@click="handleCreateOrder"
			>
				<span>{{ $t("orders-view.new-order.fields.submit") }}</span>
			</BaseButton>
			<BaseButton
				v-else
				:loading="submitRequesting"
				:disabled="!cartProducts || !cartProducts.length"
				:class="$style['continue-action']"
				type="submit"
				@click="$emit('continue', computedForm)"
			>
				<div />
				<span>{{ $t("common.buttons.continue") }}</span>
				<FontAwesomeIcon style="margin-left: 10px;" :icon="['fas', 'arrow-right']" />
			</BaseButton>
		</div>
	</DashboardModal>
</template>
<script>
import DashboardModal from '@/components/Layout/Dashboard/Modal'
import DashboardFormTitle from '@/components/Layout/Dashboard/Form/FormTitle'
import debounce from 'lodash.debounce'


import {
	mapActions as mapOrdersActions
} from '@/store/modules/OrdersOem/helpers'
import {
	CREATE_ORDER
} from '@/store/modules/OrdersOem/action-types'
import {
	mapActions as mapCartActions,
	mapGetters as mapCartGetters
} from '@/store/modules/App/Cart/helpers'
import {
	FETCH_CART_PRODUCTS,
	UPDATE_CART
} from '@/store/modules/App/Cart/action-types'


import { mapActions as mapClientsActions } from '@/store/modules/Clients/helpers'
import { FETCH_CLIENTS_SEARCH_SUGGESTIONS } from '@/store/modules/Clients/action-types'


export default {
	name: 'CartCheckoutModal',
	components: {
		DashboardModal,
		DashboardFormTitle
	},

	props: {
		oemCart: {
			type: Boolean,
			default: false
		},
		orderType: {
			type: Object,
			required: true
		}
	},

	data () {
		return {
			submitRequesting: false,
			clientSuggestionsLoading: false,
			checked: {},
			clients: [],
			form: {
				orderType: {
					name: null,
					id: null
				},
				client: {
					id: null,
					name: null
				},
				deliveryType: {
					id: 'collection',
					name: this.$t('orders-view.inner.delivery-info.fields.local')
				}
			}
		}
	},


	computed: {


		orderTypes () {
			return [
				{
					name: this.$t('cart-view.fields.order-types.order'),
					id: 'order'
				},
				{
					name: this.$t('cart-view.fields.order-types.reservation'),
					id: 'reservation'
				}
			]
		},

		orderTypeToValidate () {
			return this.form.orderType.name
		},

		deliveryTypes () {
			return [
				{
					id: 'collection',
					name: this.$t('orders-view.inner.delivery-info.fields.local')
				},
				{
					id: 'delivery',
					name: this.$t('orders-view.inner.delivery-info.fields.shipping')
				}
			]
		},

		additionalFields () {
			return [
				{
					title: `+ ${this.$t('common.actions.new-client')}`,
					// icon: ['fal', 'plus'],
					click: _e => {
						this.$emit('add-client')
					}
				}
			]
		},

		deliveryType () {
			return this.form.deliveryType.id
		},

		computedForm () {
			return {
				orderType: this.form.orderType,
				client: this.form.client ?{
					id: this.form.client.id,
					name: this.form.client.name
				} : null,
				deliveryType: this.form.deliveryType ? {
					id: this.form.deliveryType.id,
					name: this.form.deliveryType.name
				} : null
			}
		},

		cartPayload () {
			return {
				id: this.cart.id,
				clientId: this.form.client.id,
				deliveryType: this.form.deliveryType.id,
				products: this.cartProducts.map(product => ({
					id: product.recarId,
					quantity: product.quantity,
					productType: product.productType
				}))
				// deliveryType: this.form.deliveryType.id,
			}
		},

		columns () {
			return [
				{
					name: 'manufacturer',
					tag: 'parameters',
					sortKey: 'parameters',
					widthPreset: 'media',
					span: 2
				},
				{
					name: 'oem-code',
					tag: 'oem-code',
					sortKey: 'oem-code',
					widthPreset: 'id',
					span: 1
				},
				{
					name: 'price',
					tag: 'price',
					sortKey: 'price',
					span: 1
				},
				{
					name: 'quantity',
					tag: 'quantity',
					sortKey: 'quantity',
					span: 1
				},
				{
					name: 'total',
					tag: 'total',
					sortKey: 'total',
					widthPreset: 'number',
					span: 1
				}
			]
		},

		contentLoading () {
			return ['cart', 'cartProducts'].some(key => loading[key])
		},

		...mapCartGetters({
			loading: 'loading',
			cart: 'cart',
			cartProducts: 'cartProducts'
		})

		// title () {
		// 	if (this.step === 1) {
		// 		if (!this.reservation) {
		// 			return this.$t('orders-view.new-order.title.order')
		// 		} else {
		// 			return this.$t('orders-view.new-order.title.reservation')
		// 		}
		// 	}
		// 	if (this.currentStep.deliveryInfo) {
		// 		return this.$t('orders-view.new-order.title.delivery-info')
		// 	}
		// 	if (this.currentStep.clientInfo) {
		// 		return this.$t('orders-view.new-order.title.client-info')
		// 	}
		// 	return ''
		// }
	},


	created () {
		// this.$validator.pause()
		if (this.orderType.id) {
			this.form.orderType = this.orderType
		} else {
			this.form.orderType = this.orderTypes.find(type => type.id === 'order')
		}

		if (this.cart.clientId) {
			this.form.client = {
				id: this.cart.clientId,
				name: this.cart.clientName
			}
		}
		if (this.cart.deliveryType) {
			this.form.deliveryType = this.deliveryTypes.find(type => type.id === this.cart.deliveryType)
		}
		if (!this.products?.length) {
			this.handleFetchProducts()
		}
	},



	methods: {

		handleSelectRow (row) {
			if (!this.checked[row.id]) {
				this.checked[row.id] = row
			} else {
				delete this.checked[row.id]
			}
		},

		formatClientTitle (order) {
			const client = order?.client || order
			let fullname = client.name
			const nickname = client.nickname
			const companyName = client.companyName

			if (nickname) {
				fullname =  `${fullname} (${nickname})`
			}

			if (companyName) {
				fullname = `${fullname} - ${companyName}`
			}
			return fullname
		},

		productName (product) {
			return product.name || product.title || product.parameters
		},

		handleClientInput: debounce(function (e) {
			this.clientSuggestionsLoading = true
			this.fetchClientsSuggestions({ searchPhrase: e.name }).then( suggestions => {
				this.clients = suggestions.map(suggestion => ({
					id: suggestion.id,
					name: this.formatClientTitle(suggestion),
					subtitle: suggestion.phoneNumber
				}))
				this.clientSuggestionsLoading = false
			})

		}, 300),


		...mapCartActions({
			fetchProducts: FETCH_CART_PRODUCTS,
			updateCart: UPDATE_CART
		}),

		...mapOrdersActions({
			createOrder: CREATE_ORDER
		}),


		...mapClientsActions({
			fetchClientsSuggestions: FETCH_CLIENTS_SEARCH_SUGGESTIONS
		}),


		async handleUpdateCart (key = null) {
			this.$nextTick(async () => {
				try {
					await this.updateCart(this.cartPayload)
					this.$emit('refetch')
					this.handleFetchProducts()
				} catch (e) {
					if (key) {
						this.form[key] = {
							id: null,
							name: null
						}
					}
					this.$store.dispatch(
						'Notification/ADD_ERROR_NOTIFICATION',
						e
					)
				}
			})

		},

		async handleFetchProducts () {

			try {
				const payload = {
					cartIds: [this.cart.id],
					productQueryType: 'cart'
					// departmentIds: this.$auth().selectedDepartmentId ? [this.$auth().selectedDepartmentId] : null
				}
				await this.fetchProducts(payload)
			} catch (e) {
				return Promise.reject(e)
			}
		},


		async handleCreateOrder () {
			const valid = await this.$validator.validate()
			if (!valid) {
				return
			}
			this.submitRequesting = true
			try {
				const payload = {
					cartId: this.cart.id,
					departmentId: this.cart.departmentId
				}
				const { id } = await this.createOrder(payload)
				this.submitRequesting = false
				this.$emit('modal-toggle')
				this.$store.dispatch(
					'Notification/ADD_NOTIFICATION',
					{
						type: 'success',
						message: this.$t('orders-view.new-order.messages.success')
					}
				)
				this.$router.push({ name: 'Dashboard:OrderOem', params: { id } })
			} catch (e) {
				this.submitRequesting = false
				this.$store.dispatch(
					'Notification/ADD_ERROR_NOTIFICATION',
					e
				)
			}
		}



	}
}
</script>
<style lang="scss" module>
    .loading-spinner {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 200px;
        height: 300px;
        margin-left: auto;
        margin-right: auto;
    }

    .continue-action {
        margin-left: auto;
        display: flex;
        justify-content: space-between;
        min-width: 150px;
    }

    .form {
        margin-bottom: $gutter * 3;
        margin-top: $gutter * 2;
    }

    .footer {
        padding: $gutter;
        margin: $gutter 0;
        display: flex;
        justify-content: flex-end;
    }

    .color {
        &--danger {
            color: getColor(danger);
        }
    }

    .header-action {
        color: getColor(primary);
        text-decoration: none;
        font-size: 14px;
        background-color: transparent;
        border: 0;
        outline: 0;
        cursor: pointer;

        &:hover {
            color: getColor(primary-hover);
        }

        &--danger {
            color: getColor(danger);

            &:hover {
                color: getColor(danger-hover);
            }
        }
    }

    .collapsible {
        padding-bottom: 0;
    }

    .table-header {
        border-top: 1px solid getColor(border-common);
        margin-top: -20px;
        border-radius: 0;
    }

    .flex-end {
        justify-content: flex-end;
    }

    .parts-spinner {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: $gutter * 2;
    }

    .product-info {
        display: flex;
        align-items: flex-start;
        gap: $gutter;
    }

    .product-title {
        display: flex;
        flex-direction: column;
        gap: calc($gutter / 2);

        small {
            color: getColor(sub-text);
            font-size: 14px;
            line-height: 1.3;
        }
    }

    .image-wrappper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 90px;
        min-width: 90px;
        height: 70px;
        overflow: hidden;
        border-radius: 5px;
        position: relative;
        background-color: getColor(surface-overlay);
        border: 1px solid getColor(border);
        transition: border-color 0.15s ease;

        img {
            height: 100%;
            width: auto;
        }

        &--openable {
            cursor: pointer;
        }
    }

    .image-wrappper-placeholder {
        color: getColor(disabled-text);
        font-size: 24px;
    }

    .part-view {
        &__surface {
            margin-bottom: $gutter;
            padding: 0;
        }

        &__header {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: $gutter * 3;

            @include media-breakpoint-only(xs) {
                padding: $gutter * 2;
            }

            &__text {
                font-size: 18px;
                font-weight: bold;
                margin-right: calc($gutter / 2);

                &__count {
                    color: getColor(light-text);
                    padding-left: $gutter;
                }
            }

            &__info {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                color: getColor(danger);
                font-weight: 600;
                font-size: 16px;

                &__caption {
                    font-size: 12px;
                    color: getColor(caption);
                    font-weight: 400;
                }
            }
        }
    }
</style>
